import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ListBase, Pagination, FilterContext, Empty, useListContext, usePermissions, useTranslate } from 'react-admin';
import { SyncedFilterForm } from '@/components/global/filters/SyncedFilterForm.jsx';
import { getStickyLastColumnSx } from '@/themes/commonStyles/stickyLastColumn.js';
import { Card, Box, CircularProgress, useTheme, Dialog, Slide, Drawer, useMediaQuery } from '@mui/material';
import { useScrollLock } from '@/composables/useScrollLock';
import CustomTitle from '@/components/global/list/CustomTitle.jsx';
import CustomActions from '@/components/global/list/CustomActions.jsx';
import customExporter from '@/services/exporter.js';
import { useNavigate, useLocation } from 'react-router-dom';
import TagCategoryCreate from '@/components/tagCategory/TagCategoryCreate.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { ProductCreate } from '@/components/product/forms/ProductCreate.jsx';
import { ProductEdit } from '@/components/product/forms/ProductEdit.jsx';
import { ProductDetails } from '@/components/product/ProductDetails.jsx';
import { DeviceEditContent } from '@/components/device/DeviceEditContent.jsx';
import { DeviceDetailsShow } from '@/components/device/DeviceDetailsShow.jsx';
import { DeviceConfigShow } from '@/components/device/config/DeviceConfigShow.jsx';
import { EditDrawer } from '@/components/newDevice/EditDrawer.jsx';
import DeviceGroupCreate from '@/components/deviceGroup/DeviceGroupCreate.jsx';
import { DeviceGroupShow } from '@/components/deviceGroup/DeviceGroupShow.jsx';
import DeviceGroupEdit from '@/components/deviceGroup/DeviceGroupEdit.jsx';
import { DeviceErrorCreate, DeviceErrorEdit, DeviceErrorShow } from '@/pages/deviceError.jsx';
import { DeviceModelCreate, DeviceModelEdit, DeviceModelShow } from '@/pages/deviceModel.jsx';
import { ParameterCreate, ParameterEdit } from '@/pages/parameter.jsx';
import { ParameterShow } from '@/components/parameter/ParameterShow.jsx';
import { ServiceRequestCreate } from '@/pages/serviceRequest.jsx';
import { ServiceInspectionCreate } from '@/pages/serviceInspection.jsx';
import { ServiceShow } from '@/components/service/ServiceShow.jsx';
import { TransactionDetailsContent } from '@/components/transaction/TransactionDetailsContent';
import { TransactionHistory } from '@/components/transaction/TransactionHistory.jsx';
import { CollectionPointCreate } from '@/pages/collectionPoint.jsx';
import { CollectionPointDetailsShow } from '@/components/collectionPoint/CollectionPointDetailsShow.jsx';
import { MaterialEdit } from '@/pages/material';
import { MaterialShow } from '@/components/material/MaterialShow.jsx';
import { ContractorEdit, ContractorCreate } from '@/pages/contractors';
import { RetailChainCreate } from '@/components/retailChain/forms/RetailChainCreate.jsx';
import { BrandCreate } from '@/components/brand/forms/BrandCreate.jsx';
import { BrandEdit } from '@/components/brand/forms/BrandEdit.jsx';
import { BrandShow } from '@/components/brand/BrandShow.jsx';
import { RetailChainEdit } from '@/components/retailChain/forms/RetailChainEdit.jsx';
import { RetailChainShow } from '@/components/retailChain/RetailChainShow.jsx';
import { ContractorShow } from '@/components/contractors/ContractorShow.jsx';
import { ServiceCompanyCreate, ServiceCompanyShow, ServiceCompanyEdit } from '@/pages/serviceCompany';
import { SoftwareCreate, SoftwareShow, SoftwareEdit } from '@/pages/software';
import { useLastColumnShadow } from '@/composables/useLastColumnShadow';
import { DeviceIssueShow } from '@/components/deviceIssue/DeviceIssueShow.jsx';
import { UserCreate } from '@/components/users/forms/UserCreate.jsx';
import { UserEdit } from '@/components/users/forms/UserEdit.jsx';
import { UserShow } from '@/components/users/UserShow.jsx';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import TagCategoryEdit from '@/components/tagCategory/TagCategoryEdit.jsx';
import { TagCategoryShow } from '@/components/tagCategory/TagCategoryShow.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import { globalStyles } from '@/themes/commonStyles/global';
import { getActiveFilters, getGroupedActiveFilterCount } from '@/helpers/filters/activeFilters.js';
import FilterIcon from '@/assets/icons/filter-icon.svg?react';
import CollectionPointEdit from '@/components/collectionPoint/CollectionPointEdit.jsx';
import { CampaignCreate } from '@/components/promotionalCampaigns/campaign/CampaignCreate.jsx';
import { AdvertisementCreate } from '@/components/adsCampaings/AdvertisementCreate.jsx';
import { AdvertisementEdit } from '@/components/adsCampaings/AdvertisementEdit.jsx';
import { AdvertisementShow } from '@/components/adsCampaings/AdvertisementShow.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';
import { SoftwareAliasCreate, SoftwareAliasEdit } from '@/pages/softwareAlias.jsx';
import { SoftwareAliasShow } from '@/components/softwareAlias/SoftwareAliasShow.jsx';
import { SoftwareUpdateRuleCreate, SoftwareUpdateRuleEdit } from '@/pages/softwareUpdateRule.jsx';
import { SoftwareUpdateRuleShow } from '@/components/softwareUpdateRule/SoftwareUpdateRuleShow.jsx';
import { AccessKeyCreate, AccessKeyEdit } from '@/pages/accessKeys.jsx';
import { PromptShow } from '@/components/prompt/PromptShow.jsx';
import { PromptEdit } from '@/components/prompt/PromptEdit.jsx';
import { BillOfMaterialsCreate, BillOfMaterialsEdit } from '@/pages/billOfMaterials.jsx';
import { BillOfMaterialsShow } from '@/components/billOfMaterials/BillOfMaterialsShow.jsx';
import { useDeferUntilPopupEntered } from '@/composables/useDeferUntilPopupEntered.js';
import { canViewDeviceConfig } from '@/helpers/device/deviceConfig.js';
import { canViewDrawerShow, DRAWER_SHOW_TYPE_RESOURCE } from '@/helpers/permissions/canViewDetails.js';

const defaultActions = <CustomActions />;
const defaultEmpty = <Empty />;

const StyledFilterForm = styled(Box)(() => ({
    '&.filter-form': {
        display: 'flex',
        flexWrap: 'wrap',
        padding: '24px 0 17px 0',
        alignItems: 'flex-start',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            display: 'block',
        },
        '&.mobile': {
            visibility: 'hidden',
            display: 'none',
            ...globalStyles.mobileFilterActive,
        },
        '& .filter-field': {
            marginBottom: '23px',
            '&[data-source$="_range"], &[data-source="isActive"]': {
                height: '56px',
            },
            '&[data-source$="_range"]': {
                zIndex: '3',
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    height: 'inherit',
                },
            },
            '&[data-source="createdAt_range"]': {
                zIndex: '3',
                '& + .filter-field': {
                    zIndex: '2 !important',
                },
            },
            '& > .RaFilterFormInput-spacer:first-of-type': {
                display: 'none',
            },
            '& .MuiTextField-root:not(.date-input)': {
                paddingRight: '0',
                '& .MuiFormLabel-root.MuiInputLabel-shrink': {
                    color: `${Palette.gray.gray600} !important`,
                },
                '& .MuiFormLabel-root[for="deploymentStatus"]': {
                    '& + .MuiInputBase-root': {
                        minWidth: '276px',
                        maxWidth: '276px',
                        [`@media (max-width: ${breakpoints.values.md}px)`]: {
                            minWidth: '100%',
                            maxWidth: '100%',
                        },
                    },
                },
            },
            '& .MuiAutocomplete-root': {
                '& .MuiOutlinedInput-root': {
                    '& > .MuiInputBase-input:not(.MuiChip-root ~ .MuiInputBase-input)': {
                        minWidth: '200px',
                    },
                },
            },
            '& .MuiOutlinedInput-root': {
                padding: '10px 16px 10px 14px !important',
                outline: 'none',
                '&:not(.MuiAutocomplete-inputRoot)': {
                    minWidth: '220px',
                    '&.Mui-focused': {
                        boxShadow: `0 0 0 4px ${Palette.yellow.yellow400}`,
                        outline: `2px solid ${Palette.black}`,
                        '& fieldset': {
                            boxShadow: 'none !important',
                        },
                    },
                    '& .MuiInputBase-input#serialNumber': {
                        minWidth: '220px',
                    },
                },
            },
            '& .radio-button-group': {
                border: 'none',
                paddingRight: '0',
                marginRight: '8px',
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    marginRight: '0',
                },
                '& .RaRadioButtonGroupInput-label': {
                    display: 'none',
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        display: 'block',
                        paddingLeft: '0 !important',
                    },
                },
                '& .MuiFormGroup-root': {
                    borderColor: Palette.gray.gray200,
                    '& .MuiFormControlLabel-root': {
                        '&:last-child': {
                            paddingRight: '55px',
                        },
                        '& .MuiRadio-root': {
                            '& span svg.MuiSvgIcon-root': {
                                width: '20px',
                                height: '20px',
                                fill: Palette.gray.gray300,
                            },
                            '&.Mui-checked span svg.MuiSvgIcon-root': {
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10Z' fill='%23FFD342'/%3E%3Cpath d='M1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10Z' stroke='%23FFBE00' stroke-width='2'/%3E%3Cpath d='M6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10Z' fill='%23333333'/%3E%3Cpath d='M6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10Z' stroke='%231A1A1A' stroke-width='2'/%3E%3C/svg%3E")`,
                            },
                        },
                        '& .MuiFormControlLabel-label': {
                            fontSize: '11px',
                            lineHeight: '16px',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                fontSize: '14px',
                            },
                        },
                        '& + .MuiFormControlLabel-root': {
                            borderColor: Palette.gray.gray200,
                        },
                    },
                },
            },
            '& *::-webkit-scrollbar': {
                width: '12px',
            },

            '& *::-webkit-scrollbar-track': {
                background: 'transparent',
                borderRadius: '1px',
            },

            '& *::-webkit-scrollbar-thumb': {
                width: '4px',
                background: Palette.gray.gray200,
                borderRight: '8px solid transparent',
                backgroundClip: 'content-box',
                borderRadius: '1px',
            },
            '& .hide-filter': {
                backgroundColor: `${Palette.white}`,
                '&:hover': {
                    boxShadow: 'none',
                },
            },
            '& .MuiSwitch-root': {
                marginBottom: '4px',
                marginLeft: '8px',
                '& + .MuiTypography-root': {
                    fontSize: '14px',
                    margin: '-2px 30px 0px 0px',
                },
                '&.MuiSwitch-sizeSmall': {
                    height: '46px',
                },
                '& .MuiButtonBase-root': {
                    '&:hover': {
                        backgroundColor: 'transparent !important',
                    },
                },
            },
            '& .date-picker': {
                '& .date-picker-label': {
                    backgroundColor: Palette.gray.gray75,
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        backgroundColor: Palette.white,
                    },
                },
                '& .rs-picker': {
                    maxWidth: '100%',
                },
                '& + .hide-filter': {
                    top: '19px !important',
                },
            },
            '& .date-picker .rs-picker': {
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    maxWidth: '100% !important',
                    minWidth: '100% !important',
                    '& .rs-input': {
                        fontSize: '16px',
                    },
                },
            },
        },
        '& .search-bar--hidden': {
            display: 'none !important',
        },
        '& .filter-mobile-action': {
            display: 'none',
        },
    },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction='left' ref={ref} {...props} />;
});

const DeferredDialogContent = ({ renderContent }) => {
    const { containerRef, isPopupEntered } = useDeferUntilPopupEntered();

    return (
        <Box ref={containerRef}>
            {isPopupEntered ? (
                renderContent()
            ) : (
                <Box sx={{ minHeight: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            )}
        </Box>
    );
};

DeferredDialogContent.propTypes = {
    renderContent: PropTypes.func.isRequired,
};

export const getDrawerContent = (type, elementId, handleClose, loadingLabel, drawerSection) => {
    const contentMap = {
        'product-show': ProductDetails,
        'product-create': ProductCreate,
        'device-show': DeviceDetailsShow,
        'device-config': DeviceConfigShow,
        'device-edit': DeviceEditContent,
        'device-group-show': DeviceGroupShow,
        'device-group-edit': DeviceGroupEdit,
        'device-error-show': DeviceErrorShow,
        'device-error-edit': DeviceErrorEdit,
        'device-model-show': DeviceModelShow,
        'device-model-edit': DeviceModelEdit,
        'parameter-edit': ParameterEdit,
        'parameter-show': ParameterShow,
        'service-request-create': ServiceRequestCreate,
        'service-inspection-create': ServiceInspectionCreate,
        'device-issue-show': DeviceIssueShow,
        'transaction-show': TransactionDetailsContent,
        'test-transaction-show': TransactionDetailsContent,
        'transaction-history': TransactionHistory,
        'user-edit': UserEdit,
        'user-show': UserShow,
        'new-device-edit': EditDrawer,
        'tag-category-edit': TagCategoryEdit,
        'tag-category-show': TagCategoryShow,
        'collection-point-show': CollectionPointDetailsShow,
        'collection-point-edit': CollectionPointEdit,
        'product-edit': ProductEdit,
        'material-edit': MaterialEdit,
        'material-show': MaterialShow,
        'contractor-edit': ContractorEdit,
        'contractor-show': ContractorShow,
        'retail-chain-show': RetailChainShow,
        'retail-chain-edit': RetailChainEdit,
        'brand-edit': BrandEdit,
        'brand-show': BrandShow,
        'service-company-show': ServiceCompanyShow,
        'service-company-edit': ServiceCompanyEdit,
        'software-show': SoftwareShow,
        'software-edit': SoftwareEdit,
        'software-alias-edit': SoftwareAliasEdit,
        'software-alias-show': SoftwareAliasShow,
        'software-update-rule-edit': SoftwareUpdateRuleEdit,
        'software-update-rule-show': SoftwareUpdateRuleShow,
        'access-key-create': AccessKeyCreate,
        'access-key-edit': AccessKeyEdit,
        'ad-edit': AdvertisementEdit,
        'ad-show': AdvertisementShow,
        'prompt-show': PromptShow,
        'prompt-edit': PromptEdit,
        'spare-part-create': BillOfMaterialsCreate,
        'spare-part-edit': BillOfMaterialsEdit,
        'spare-part-show': BillOfMaterialsShow,
    };

    const Component = type?.startsWith('service-request-edit') || type?.startsWith('service-inspection-edit') ? ServiceShow : contentMap[type];

    if (Component) {
        return elementId ? (
            <Component handleClose={handleClose} elementId={elementId} type={type} drawerSection={drawerSection} />
        ) : (
            <div className='loading'>{loadingLabel}</div>
        );
    } else {
        return null;
    }
};

export const getModalDialogClass = () => {
    const modalDialogClassMap = {
        '/tag-category': 'custom-dialog-right--medium',
        '/device-group': 'custom-dialog-right--medium',
        '/device-error': 'custom-dialog-right--medium',
        '/device-model': 'custom-dialog-right--medium',
        '/service-request': 'custom-dialog-right--small',
        '/service-inspection': 'custom-dialog-right--small',
        '/product': 'custom-dialog-right--medium',
        '/user': 'custom-dialog-right--medium',
        '/collection-point': 'custom-dialog-right--medium',
        '/collection-point-map': 'custom-dialog-right custom-dialog-right--wide',
        '/promotional-campaigns': 'custom-dialog-right--wide',
        '/ad': 'custom-dialog-right--medium',
    };

    return modalDialogClassMap[location.pathname] || 'custom-dialog-right--medium';
};

const CustomListContent = ({
    children,
    actions = defaultActions,
    filters,
    title,
    empty = defaultEmpty,
    disablePagination = false,
    paginationClassName = '',
    rowsPerPageOptions = [50, 100],
    addLastColumnShadow = false,
}) => {
    const theme = useTheme();
    const translate = useTranslate();
    const { permissions, isLoading: isPermissionsLoading } = usePermissions();
    const { data, isPending, filterValues, total = 0, perPage } = useListContext();
    const canManageAd = (permissions?.isAdmin || permissions?.isClient) && !permissions?.isPosOwner;
    const canViewConfig = canViewDeviceConfig(permissions);
    const isFilterEmpty = Object.keys(filterValues).length === 0;
    const isPeriodActiveAndNotCustom = filterValues.period && filterValues.period !== 'custom';
    const isFilterFormEmpty = isFilterEmpty || isPeriodActiveAndNotCustom;
    const activeFilters = getActiveFilters(filterValues);
    const groupedActiveFilterCount = getGroupedActiveFilterCount(activeFilters);
    const scrollContainerRef = useRef(null);
    const isTableReady = !isPending && !!data;
    const { showShadow } = useLastColumnShadow(scrollContainerRef, isTableReady);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isMobileActive, setIsMobileActive] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const isDialogOpen = location.hash === '#create';
    const { isDrawerOpen, elementId, closeDrawer, closeAllDrawers, type, section } = useDrawer();
    const isAdEditPendingAuth = isPermissionsLoading && type === 'ad-edit';
    const isAdEditBlocked = !isPermissionsLoading && type === 'ad-edit' && !canManageAd;
    const isTransactionHistoryPendingAuth = isPermissionsLoading && type === 'transaction-history';
    const isTransactionHistoryBlocked = !isPermissionsLoading && type === 'transaction-history' && !permissions?.isAdmin;
    const isDeviceConfigPendingAuth = isPermissionsLoading && type === 'device-config';
    const isDeviceConfigBlocked = !isPermissionsLoading && type === 'device-config' && !canViewConfig;
    const isDetailsShowType = Boolean(DRAWER_SHOW_TYPE_RESOURCE[type]);
    const isDetailsPendingAuth = isPermissionsLoading && isDetailsShowType;
    const isDetailsBlocked = !isPermissionsLoading && isDetailsShowType && !canViewDrawerShow(type, permissions);
    const isDrawerVisible =
        isDrawerOpen &&
        type != null &&
        type !== '' &&
        !isAdEditBlocked &&
        !isAdEditPendingAuth &&
        !isTransactionHistoryBlocked &&
        !isTransactionHistoryPendingAuth &&
        !isDeviceConfigBlocked &&
        !isDeviceConfigPendingAuth &&
        !isDetailsBlocked &&
        !isDetailsPendingAuth;
    const isOverlayOpen = isDialogOpen || isDrawerVisible;

    useScrollLock(isOverlayOpen);

    useEffect(() => {
        if (isMobile && filterValues.period === 'custom') {
            setIsMobileActive(true);
        }
    }, [isMobile, filterValues.period]);

    useEffect(() => {
        if (isAdEditBlocked || isTransactionHistoryBlocked || isDeviceConfigBlocked || isDetailsBlocked) {
            closeAllDrawers();
        }
    }, [isAdEditBlocked, isTransactionHistoryBlocked, isDeviceConfigBlocked, isDetailsBlocked, closeAllDrawers]);

    const getDialogClass = type => {
        const dialogClassMap = {
            'new-device-edit': 'custom-dialog-right--medium',
            'device-edit': 'custom-dialog-right--wide',
            'device-show': 'custom-dialog-right--wide',
            'device-config': 'custom-dialog-right--wide',
            'device-group-show': 'custom-dialog-right--wide',
            'device-group-edit': 'custom-dialog-right--medium',
            'device-error-show': 'custom-dialog-right--wide',
            'device-error-edit': 'custom-dialog-right--wide',
            'device-model-show': 'custom-dialog-right--wide',
            'device-model-edit': 'custom-dialog-right--wide',
            'service-request-create': 'custom-dialog-right--small',
            'service-inspection-create': 'custom-dialog-right--small',
            'device-issue-show': 'custom-dialog-right--medium',
            'transaction-show': 'custom-dialog-right--wide',
            'test-transaction-show': 'custom-dialog-right--wide',
            'transaction-history': 'custom-dialog-right--wide',
            'user-create': 'custom-dialog-right--medium',
            'user-edit': 'custom-dialog-right--medium',
            'user-show': 'custom-dialog-right--medium',
            'product-edit': 'custom-dialog-right--medium',
            'tag-category-edit': 'custom-dialog-right--medium',
            'tag-category-show': 'custom-dialog-right--medium',
            'collection-point-show': 'custom-dialog-right--wide',
            'collection-point-edit': 'custom-dialog-right--wide',
            'ad-edit': 'custom-dialog-right--medium',
            'ad-show': 'custom-dialog-right--medium',
            'contractor-show': 'custom-dialog-right--wide',
            'retail-chain-show': 'custom-dialog-right--wide',
            'retail-chain-edit': 'custom-dialog-right--wide',
            'brand-edit': 'custom-dialog-right--wide',
            'brand-show': 'custom-dialog-right--wide',
            'parameter-show': 'custom-dialog-right--wide',
            'access-key-edit': 'custom-dialog-right--medium custom-dialog-right--no-overflow',
        };

        if (type?.startsWith('service-request-edit') || type?.startsWith('service-inspection-edit')) {
            return 'custom-dialog-right--medium';
        }

        return dialogClassMap[type] || 'custom-dialog-right--medium';
    };

    const handleClose = () => {
        navigate({ pathname: location.pathname, search: location.search }, { replace: true });
    };

    const renderModalContent = () => {
        if (location.pathname === '/tag-category' && location.hash === '#create') {
            return <TagCategoryCreate handleClose={handleClose} />;
        } else if (location.pathname === '/device-group' && location.hash === '#create') {
            return <DeviceGroupCreate handleClose={handleClose} />;
        } else if (location.pathname === '/device-error' && location.hash === '#create') {
            return <DeviceErrorCreate handleClose={handleClose} />;
        } else if (location.pathname === '/device-model' && location.hash === '#create') {
            return <DeviceModelCreate handleClose={handleClose} />;
        } else if (location.pathname === '/parameter' && location.hash === '#create' && permissions?.isAdmin) {
            return <ParameterCreate handleClose={handleClose} />;
        } else if (location.pathname === '/service-request' && location.hash === '#create') {
            return <ServiceRequestCreate handleClose={handleClose} />;
        } else if (location.pathname === '/service-inspection' && location.hash === '#create') {
            return <ServiceInspectionCreate handleClose={handleClose} />;
        } else if (location.pathname === '/product' && location.hash === '#create') {
            return <ProductCreate handleClose={handleClose} />;
        } else if (location.pathname === '/user' && location.hash === '#create') {
            return <UserCreate handleClose={handleClose} />;
        } else if (location.pathname === '/collection-point' && location.hash === '#create') {
            return <CollectionPointCreate handleClose={handleClose} />;
        } else if (location.pathname === '/contractor' && location.hash === '#create') {
            return <ContractorCreate handleClose={handleClose} />;
        } else if (location.pathname === '/retail-chain' && location.hash === '#create' && permissions?.isAdmin) {
            return <RetailChainCreate handleClose={handleClose} />;
        } else if (location.pathname === '/brand' && location.hash === '#create' && permissions?.isAdmin) {
            return <BrandCreate handleClose={handleClose} />;
        } else if (location.pathname === '/service-company' && location.hash === '#create') {
            return <ServiceCompanyCreate handleClose={handleClose} />;
        } else if (location.pathname === '/software' && location.hash === '#create') {
            return <SoftwareCreate handleClose={handleClose} />;
        } else if (location.pathname === '/software-alias' && location.hash === '#create') {
            return <SoftwareAliasCreate handleClose={handleClose} />;
        } else if (location.pathname === '/software-update-rule' && location.hash === '#create') {
            return <SoftwareUpdateRuleCreate handleClose={handleClose} />;
        } else if (location.pathname === '/promotional-campaigns' && location.hash === '#create') {
            return <CampaignCreate handleClose={handleClose} />;
        } else if (location.pathname === '/ad' && location.hash === '#create' && canManageAd) {
            return <AdvertisementCreate handleClose={handleClose} />;
        } else if (location.pathname === '/access-key' && location.hash === '#create') {
            return <AccessKeyCreate handleClose={handleClose} />;
        } else if (location.pathname === '/spare-part' && location.hash === '#create') {
            return <BillOfMaterialsCreate handleClose={handleClose} />;
        } else {
            return null;
        }
    };

    const shouldRenderEmptyPage = !isPending && data?.length === 0 && !Object.keys(filterValues).length && empty !== false;
    const shouldShowTopFilters = location.pathname.startsWith('/analytic');
    const shouldShowMobileFilterButton =
        isMobile && location.pathname !== '/tag-category' && location.pathname !== '/ad' && location.pathname !== '/contractor';
    const shouldShowMobileActions = shouldShowMobileFilterButton && actions;

    if (!children || (!data && isPending)) {
        return null;
    }

    return (
        <>
            {shouldRenderEmptyPage ? (
                empty
            ) : (
                <>
                    <FilterContext.Provider value={filters}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px',
                                [theme.breakpoints.down('md')]: {
                                    display: 'block',
                                },
                                '& .MuiToolbar-root': {
                                    [theme.breakpoints.down('md')]: {
                                        background: 'transparent !important',
                                    },
                                },
                                '& .filter-button, & .button--icon-only.white': {
                                    [theme.breakpoints.down('md')]: {
                                        display: !shouldShowTopFilters ? 'none' : 'block',
                                    },
                                },
                            }}
                        >
                            <CustomTitle title={title} />
                            <Box sx={{ flex: 1 }} />
                            <Box
                                className={`actions-wrapper ${shouldShowMobileActions ? 'actions-wrapper--actions-right' : ''}`}
                                sx={globalStyles.headerActionsWrapper}
                            >
                                {actions && (
                                    <Box className={`actions-wrapper__desktop ${location.pathname === '/ad' ? 'actions-wrapper__desktop--ad' : ''}`}>
                                        {actions}
                                    </Box>
                                )}
                                {shouldShowMobileActions ? actions : null}
                                {shouldShowMobileFilterButton && (
                                    <CustomButton
                                        onClick={() => setIsMobileActive(prev => !prev)}
                                        startIcon={<FilterIcon />}
                                        label={
                                            isFilterEmpty
                                                ? translate('common.filter.filters')
                                                : `${translate('common.filter.filters')} (${groupedActiveFilterCount})`
                                        }
                                        size='small'
                                        variant='contained'
                                        className={`mobile-filter-button ${isMobileActive ? ' active' : ''}`}
                                    />
                                )}
                            </Box>
                        </Box>
                        <StyledFilterForm className={`filter-form ${isMobile ? 'mobile' : ''} ${isMobileActive ? 'mobile-active' : ''}`}>
                            <SyncedFilterForm className={isFilterFormEmpty ? 'empty' : ''} />
                            <Box className='filter-mobile-action'>
                                <CustomButton
                                    onClick={() => setIsMobileActive(false)}
                                    label={translate('common.apply')}
                                    size='small'
                                    variant='contained'
                                    fullWidth
                                    className='filter-mobile-action__button'
                                />
                            </Box>
                        </StyledFilterForm>
                    </FilterContext.Provider>
                    <Drawer
                        anchor='right'
                        open={isDrawerVisible}
                        onClose={closeAllDrawers}
                        PaperProps={{ className: `custom-dialog-right ${getDialogClass(type)}` }}
                        ModalProps={{ disableAutoFocus: true }}
                        disableScrollLock
                    >
                        <Box className='custom-dialog-right__inner'>
                            {isDrawerVisible ? getDrawerContent(type, elementId, closeDrawer, translate('common.loading'), section) : null}
                        </Box>
                    </Drawer>

                    <Dialog
                        open={isDialogOpen}
                        onClose={handleClose}
                        TransitionComponent={Transition}
                        PaperProps={{ className: `custom-dialog-right ${getModalDialogClass()}` }}
                        disableEnforceFocus
                        disableAutoFocus
                        disablePortal
                        disableScrollLock
                    >
                        <Box className='custom-dialog-right__inner'>{isDialogOpen && <DeferredDialogContent renderContent={renderModalContent} />}</Box>
                    </Dialog>

                    <Card className='scroll-container' ref={scrollContainerRef} sx={getStickyLastColumnSx(addLastColumnShadow, showShadow)}>
                        {children}
                    </Card>
                    {!disablePagination && total > perPage && <Pagination rowsPerPageOptions={rowsPerPageOptions} className={paginationClassName} />}
                </>
            )}
        </>
    );
};

CustomListContent.propTypes = {
    children: PropTypes.node,
    actions: PropTypes.element,
    filters: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    title: PropTypes.string,
    empty: PropTypes.node,
    disablePagination: PropTypes.bool,
    paginationClassName: PropTypes.string,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    addLastColumnShadow: PropTypes.bool,
    dialogClass: PropTypes.string,
};

const defaultFilter = { withAggregates: true };

const CustomList = ({
    debounce,
    disableAuthentication,
    disableSyncWithLocation,
    filter = defaultFilter,
    filterDefaultValues,
    perPage = 50,
    queryOptions,
    resource,
    sort,
    storeKey,
    disablePagination = false,
    paginationClassName = '',
    rowsPerPageOptions = [50, 100],
    addLastColumnShadow = false,
    dialogClass,
    ...rest
}) => (
    <ListBase
        debounce={debounce}
        disableAuthentication={disableAuthentication}
        disableSyncWithLocation={disableSyncWithLocation}
        exporter={customExporter}
        filter={filter}
        filterDefaultValues={filterDefaultValues}
        perPage={perPage}
        queryOptions={queryOptions}
        resource={resource}
        sort={sort}
        storeKey={storeKey}
    >
        <CustomListContent
            disablePagination={disablePagination}
            paginationClassName={paginationClassName}
            rowsPerPageOptions={rowsPerPageOptions}
            addLastColumnShadow={addLastColumnShadow}
            dialogClass={dialogClass}
            {...rest}
        />
    </ListBase>
);

export default CustomList;
